import type { ReactNode } from "react"
import { CheckIcon, TriangleAlert } from "lucide-react"

import { cn } from "@/lib/utils"

export interface WizardSection {
  value: string
  label: string
  complete: boolean
  invalid?: boolean
}

/**
 * document-editor.md's `DocumentWizardNav` — persistent section navigation,
 * completion/validation state, and the wizard's own non-destructive actions
 * (`actions`, composed by the caller: consent download, preview, sign).
 *
 * Deviation from the documented contract, flagged rather than silently
 * approximated: the spec's rail is built on the reui `Stepper` primitive
 * (`npx shadcn@latest add @reui/stepper` + the `c-stepper-9`/`c-stepper-15`
 * registry blocks), none of which are installed in this package yet. This
 * renders the same two-tree structure (a horizontal compact group below
 * `lg`, a 272px sticky vertical rail at `lg` and up) and copies every
 * documented color/size/state value (`success`-token completed tone, `bg-primary`
 * selected tone, destructive invalid triangle, 24px indicator, 272px rail,
 * `top: 16px` sticky offset) using plain buttons instead of the `Stepper`
 * primitive. Installing the real primitive is follow-up work, not done
 * silently here.
 */
export function DocumentWizardNav({
  sections,
  activeValue,
  onValueChange,
  actions,
}: {
  sections: WizardSection[]
  activeValue: string
  onValueChange: (value: string) => void
  actions: ReactNode
}) {
  return (
    <div className="flex flex-col gap-4 lg:sticky lg:top-4 lg:h-[calc(100vh-2rem)] lg:w-[272px] lg:shrink-0">
      <div className="overflow-x-auto overflow-y-hidden lg:overflow-x-hidden lg:overflow-y-auto">
        <StepperList
          activeValue={activeValue}
          onValueChange={onValueChange}
          orientation="horizontal"
          sections={sections}
        />
        <StepperList
          activeValue={activeValue}
          onValueChange={onValueChange}
          orientation="vertical"
          sections={sections}
        />
      </div>
      <div
        className={cn(
          "grid grid-flow-col auto-cols-fr gap-2 lg:flex lg:flex-col lg:gap-2",
          "[&_[data-slot=button]]:lg:h-9 [&_[data-slot=button]]:lg:px-2.5 [&_[data-slot=button]]:lg:text-sm",
          "[&_[data-slot=dropdown-menu-trigger]]:lg:h-9 [&_[data-slot=dropdown-menu-trigger]]:lg:px-2.5 [&_[data-slot=dropdown-menu-trigger]]:lg:text-sm"
        )}
      >
        {actions}
      </div>
    </div>
  )
}

function StepperList({
  sections,
  activeValue,
  onValueChange,
  orientation,
}: {
  sections: WizardSection[]
  activeValue: string
  onValueChange: (value: string) => void
  orientation: "horizontal" | "vertical"
}) {
  return (
    <div
      className={cn(
        orientation === "horizontal"
          ? "flex w-full items-start gap-1.5 lg:hidden"
          : "hidden w-full flex-col lg:flex"
      )}
    >
      {sections.map((section, index) => {
        const selected = section.value === activeValue
        const invalid = Boolean(section.invalid)
        const completed = !invalid && section.complete
        const isLast = index === sections.length - 1

        return (
          <div
            className={cn(
              "relative flex shrink-0 items-start",
              orientation === "horizontal" ? "flex-col gap-1.5" : "w-full flex-1"
            )}
            key={section.value}
          >
            <button
              className={cn(
                "group flex items-center gap-2.5",
                orientation === "horizontal"
                  ? "flex-col gap-1.5"
                  : "w-full pb-4 last:pb-0"
              )}
              onClick={() => onValueChange(section.value)}
              type="button"
            >
              <span
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-medium text-accent-foreground",
                  completed &&
                    "bg-success/10 text-success dark:bg-success/20",
                  selected && "bg-primary! text-primary-foreground!"
                )}
              >
                {completed ? (
                  <CheckIcon className="size-3.5" />
                ) : invalid ? (
                  <TriangleAlert
                    className={cn(
                      "size-3.5",
                      selected ? "text-primary-foreground" : "text-destructive"
                    )}
                  />
                ) : (
                  index + 1
                )}
              </span>
              <span
                className={cn(
                  "text-sm transition-colors duration-200 group-hover:text-primary",
                  selected
                    ? "font-semibold text-foreground"
                    : "font-normal text-muted-foreground",
                  orientation === "horizontal" && "whitespace-nowrap"
                )}
              >
                {section.label}
              </span>
            </button>
            {orientation === "vertical" && !isLast && (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute top-6 left-3 -z-10 h-[calc(100%-1.5rem)] w-0.5 -translate-x-1/2 bg-muted",
                  completed && "bg-primary"
                )}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
